"use client";

import { createContext, useContext, useState, useCallback } from 'react';
import { getDocuments } from '@/lib/firebase/firebaseUtils';
import type { MapDetails } from '@/types/map';
import type { CustomerDetails } from '@/types/customer';

interface SearchResult {
  type: 'map' | 'customer' | 'marker';
  id: string;
  title: string;
  subtitle?: string;
  link: string;
  matchedTerm?: string;
}

interface SearchContextType {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  results: SearchResult[];
  isSearching: boolean;
  performSearch: (term: string) => Promise<void>;
}

const SearchContext = createContext<SearchContextType | undefined>(undefined);

export function SearchProvider({ children }: { children: React.ReactNode }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const performSearch = useCallback(async (term: string) => {
    setIsSearching(true);
    
    try {
      // If search term is empty, still fetch initial results
      const [maps, customers] = await Promise.all([
        getDocuments<MapDetails>('maps'),
        getDocuments<CustomerDetails>('customers')
      ]);

      if (!term.trim()) {
        // Show recent or all items when search is empty
        const initialResults: SearchResult[] = [
          ...customers.slice(0, 3).map(customer => ({
            type: 'customer',
            id: customer.id,
            title: customer.name,
            subtitle: `Customer - ${customer.type}`,
            link: `/customers/${customer.id}`,
          })),
          ...maps.slice(0, 3).map(map => ({
            type: 'map',
            id: map.id,
            title: map.name,
            subtitle: `Map - ${map.customer?.name || 'Unknown Customer'}`,
            link: `/maps/${map.id}`,
          }))
        ];
        setResults(initialResults);
        return;
      }

      const searchLower = term.toLowerCase();
      const searchResults: SearchResult[] = [];

      // Search through maps
      maps.forEach(map => {
        if (map.name.toLowerCase().includes(searchLower)) {
          searchResults.push({
            type: 'map',
            id: map.id,
            title: map.name,
            subtitle: `Map - ${map.customer?.name || 'Unknown Customer'}`,
            link: `/maps/${map.id}`,
            matchedTerm: 'name'
          });
        }

        // Search through markers
        map.markers?.forEach(marker => {
          if (
            marker.label?.toLowerCase().includes(searchLower) ||
            marker.notes?.toLowerCase().includes(searchLower)
          ) {
            searchResults.push({
              type: 'marker',
              id: marker.id,
              title: marker.label || 'Unnamed Marker',
              subtitle: `Marker in ${map.name}`,
              link: `/maps/${map.id}/edit?marker=${marker.id}`,
              matchedTerm: marker.label?.toLowerCase().includes(searchLower) ? 'label' : 'notes'
            });
          }
        });
      });

      // Search through customers
      customers.forEach(customer => {
        if (
          customer.name.toLowerCase().includes(searchLower) ||
          customer.notes?.toLowerCase().includes(searchLower)
        ) {
          searchResults.push({
            type: 'customer',
            id: customer.id,
            title: customer.name,
            subtitle: `Customer - ${customer.type}`,
            link: `/customers/${customer.id}`,
            matchedTerm: customer.name.toLowerCase().includes(searchLower) ? 'name' : 'notes'
          });
        }
      });

      setResults(searchResults);
    } catch (error) {
      console.error('Search error:', error);
      setResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  return (
    <SearchContext.Provider value={{
      searchTerm,
      setSearchTerm,
      results,
      isSearching,
      performSearch
    }}>
      {children}
    </SearchContext.Provider>
  );
}

export function useSearch() {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
} 